import type { ReactNode } from 'react';
import { useT } from '../../i18n/i18n';
import type { IconName } from '../icons';
import { IconButton } from './Button';
import { Icon } from './Icon';

// List row card (01 area rows · 04 / 05 item rows): optional drag handle · icon tile · title ·
// subtitle · trailing (pill / progress / count) · chevron · ⋮ menu; selected = tinted ground with
// a left rail in the status tone. The main hit area is one button; the menu is a second button
// so the markup stays valid and both stay ≥48px targets.
export function ListRow(props: {
  title: string;
  subtitle?: string;
  icon?: IconName;
  iconTone?: 'default' | 'error' | 'soft';
  trailing?: ReactNode;
  // Rendered in front of the main hit area as its own control (e.g. a cover thumbnail that opens a viewer).
  leading?: ReactNode;
  onClick?: () => void;
  tone?: 'default' | 'attention' | 'done';
  selected?: boolean;
  rail?: 'primary' | 'error';
  chevron?: boolean;
  handle?: boolean;
  menu?: { label?: string; onClick: () => void; testId?: string };
  // Stable identity for tests / automation (data-name on the row root), e.g. the area name.
  dataName?: string;
  testId?: string;
}) {
  const { t } = useT();
  const className = [
    'list-row',
    props.tone === 'attention' ? 'list-row-attention' : '',
    props.tone === 'done' ? 'list-row-done' : '',
    props.selected ? 'list-row-selected' : '',
    props.rail ? `list-row-rail list-row-rail-${props.rail}` : ''
  ]
    .filter((c) => c.length > 0)
    .join(' ');
  const tileClass = props.iconTone === 'error' ? 'icon-tile icon-tile-error' : props.iconTone === 'soft' ? 'icon-tile icon-tile-soft' : 'icon-tile';
  return (
    <div className={className} aria-selected={props.selected ? true : undefined} data-name={props.dataName}>
      {props.handle ? (
        <span className="list-row-handle" aria-label={t('common.dragToReorder')} role="img">
          <Icon name="drag_indicator" />
        </span>
      ) : null}
      {props.leading ?? null}
      <button type="button" className={`list-row-hit ${props.onClick ? '' : 'list-row-static'}`} onClick={props.onClick} data-testid={props.testId}>
        {props.icon ? (
          <span className={tileClass}>
            <Icon name={props.icon} />
          </span>
        ) : null}
        <span className="list-row-main">
          <span className="list-row-title">{props.title}</span>
          {props.subtitle ? <span className="list-row-subtitle">{props.subtitle}</span> : null}
        </span>
        <span className="list-row-trailing">
          {props.trailing}
          {props.chevron ? <Icon name="chevron_right" className="list-row-chevron" /> : null}
        </span>
      </button>
      {props.menu ? (
        <IconButton icon="more_vert" label={props.menu.label ?? t('common.more')} onClick={props.menu.onClick} tone="soft" className="list-row-menu" testId={props.menu.testId} />
      ) : null}
    </div>
  );
}
